import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PublicShell from "@/layouts/PublicShell";
import StateBadge from "@/components/StateBadge";
import api from "@/lib/api";
import {
  ShieldCheck,
  Tv,
  BarChart3,
  Calendar,
  AlertTriangle,
  MapPin,
} from "lucide-react";

/**
 * /audit/:handle — public audit archive for a media owner.
 * Every play is signed — the archive shows 12 weeks of signed totals per
 * screen. No login required. Auditable by anyone.
 */

export default function AuditArchive() {
  const { handle } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/audit/${handle}`);
        setData(r.data);
      } catch (e) {
        setError(
          e?.response?.status === 404
            ? "No audit archive is published under this handle."
            : "Could not load archive.",
        );
      }
    })();
  }, [handle]);

  if (error) {
    return (
      <PublicShell>
        <div className="pt-40 pb-32 text-center px-6">
          <AlertTriangle size={32} className="text-red-400 mx-auto mb-4" />
          <div data-testid="audit-error" className="text-white/60 text-lg">
            {error}
          </div>
        </div>
      </PublicShell>
    );
  }
  if (!data) {
    return (
      <PublicShell>
        <div className="pt-40 pb-32 text-center text-white/40">
          Loading archive…
        </div>
      </PublicShell>
    );
  }

  const maxWeekly = Math.max(1, ...data.weekly.map((w) => w.plays));

  return (
    <PublicShell>
      <section className="pt-28 md:pt-32 border-b border-white/10">
        <div
          data-testid="audit-page"
          className="max-w-[1400px] mx-auto px-6 md:px-10 pb-14"
        >
          <div className="flex items-center gap-3 mb-4">
            <StateBadge state="LIVE" />
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/40">
              Public audit archive
            </span>
          </div>
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <h1
                data-testid="audit-owner-name"
                className="font-display font-black tracking-tighter text-4xl md:text-6xl leading-[0.95]"
              >
                {data.owner_name}
              </h1>
              <div
                data-testid="audit-handle"
                className="text-[#E8D6A3] font-mono text-sm mt-2"
              >
                @{data.handle}
              </div>
              <p className="text-white/60 mt-4 max-w-2xl">
                A public, cryptographically-signed record of every playback
                delivered on this media owner's screens. Every row in the
                ledger is signed with{" "}
                <span className="font-mono text-[#E8D6A3]">
                  {data.algorithm}
                </span>
                . Independently verify any row at{" "}
                <Link to="/verify" className="underline hover:text-[#E8D6A3]">
                  /verify
                </Link>
                .
              </p>
            </div>
            <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10">
              <BigTile
                icon={<BarChart3 size={13} />}
                k="12-week plays"
                v={data.total_plays_12w.toLocaleString()}
                accent
              />
              <BigTile
                icon={<Tv size={13} />}
                k="Screens"
                v={data.screens_count}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-4 flex items-center gap-2">
            <Calendar size={12} /> Weekly plays · last 12 ISO-weeks
          </div>
          {data.weekly.length === 0 ? (
            <div className="p-8 border border-white/10 bg-white/[0.02] text-white/40">
              No plays observed in the last 12 weeks.
            </div>
          ) : (
            <div
              data-testid="audit-weekly"
              className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-2"
            >
              {data.weekly.map((w) => {
                const pct = Math.max(4, Math.round((w.plays / maxWeekly) * 100));
                return (
                  <div
                    key={w.label}
                    className="border border-white/10 bg-white/[0.02] p-3 flex flex-col justify-end min-h-[120px] relative overflow-hidden"
                  >
                    <div
                      className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#D4B878] to-[#E8D6A3]/40 opacity-30"
                      style={{ height: `${pct}%` }}
                    />
                    <div className="relative">
                      <div className="text-[10px] font-mono text-white/40">
                        {w.label}
                      </div>
                      <div className="text-lg font-black text-white leading-tight">
                        {w.plays.toLocaleString()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-4 flex items-center gap-2">
            <MapPin size={12} /> Plays per screen
          </div>
          {data.screens.length === 0 ? (
            <div className="p-8 border border-white/10 bg-white/[0.02] text-white/40">
              No screens have logged plays yet.
            </div>
          ) : (
            <div
              data-testid="audit-screens"
              className="border border-white/10 divide-y divide-white/10"
            >
              {data.screens.map((s) => (
                <div
                  key={s.screen_id}
                  className="p-4 flex items-center gap-4"
                  data-testid={`audit-screen-${s.screen_id}`}
                >
                  <Tv size={16} className="text-white/40" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-white truncate">
                      {s.name}
                    </div>
                    {s.location && (
                      <div className="text-xs text-white/40 truncate">
                        {s.location}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-black text-[#E8D6A3]">
                      {s.plays.toLocaleString()}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">
                      12w plays
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 text-center">
          <ShieldCheck size={28} className="text-emerald-400 mx-auto mb-3" />
          <div className="text-white/60 text-sm max-w-xl mx-auto">
            Every play on the network is signed by{" "}
            <span className="font-mono text-[#E8D6A3]">{data.signed_by}</span>.
            Anyone with a signed row can independently verify it at{" "}
            <Link to="/verify" className="underline hover:text-[#E8D6A3]">
              /verify
            </Link>
            .
          </div>
        </div>
      </section>
    </PublicShell>
  );
}

function BigTile({ icon, k, v, accent }) {
  return (
    <div
      className={`px-6 py-5 ${
        accent
          ? "bg-gradient-to-br from-[#1a1508] to-[#0c0c0c]"
          : "bg-[#0A0908]"
      }`}
    >
      <div className="text-[#E8D6A3] mb-2">{icon}</div>
      <div className="text-3xl font-black text-white">{v}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mt-1">
        {k}
      </div>
    </div>
  );
}
